import React, { useState } from 'react';
import { UserRankData } from '../api/ranking';

interface RankBadgeProps {
  rankData: UserRankData | null;
  size?: 'sm' | 'md';
  showTitle?: boolean;
}

const RankBadge: React.FC<RankBadgeProps> = ({ rankData, size = 'md', showTitle = true }) => {
  const [hovered, setHovered] = useState(false);

  if (!rankData) return null;

  const { rank, score, nextRank, progressToNext } = rankData;

  const pillClass =
    size === 'sm'
      ? 'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border backdrop-blur-sm cursor-default relative'
      : 'inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-semibold border backdrop-blur-sm cursor-default relative';

  const colorMap: Record<string, string> = {
    seed: 'bg-emerald-500/15 border-emerald-400/40 text-emerald-300',
    builder: 'bg-amber-500/15 border-amber-400/40 text-amber-300',
    connector: 'bg-cyan-500/15 border-cyan-400/40 text-cyan-300',
    architect: 'bg-indigo-500/15 border-indigo-400/40 text-indigo-300',
    innovator: 'bg-violet-500/15 border-violet-400/40 text-violet-300',
    pioneer: 'bg-pink-500/15 border-pink-400/40 text-pink-300',
    luminary: 'bg-fuchsia-500/15 border-fuchsia-400/40 text-fuchsia-300',
    visionary: 'bg-amber-400/15 border-amber-300/40 text-amber-200',
  };

  const colorClass = colorMap[rank.id] || 'bg-tech-cyan/10 border-tech-cyan/30 text-tech-cyan';

  return (
    <span
      className={`${pillClass} ${colorClass}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <span role="img" aria-label={rank.title}>{rank.emoji}</span>
      {showTitle && <span>{rank.title}</span>}

      {/* Tooltip */}
      {hovered && (
        <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-50 w-48 rounded-xl border border-tech-border bg-tech-surface/95 backdrop-blur-xl shadow-[0_8px_32px_rgba(0,0,0,0.5)] p-3 text-left pointer-events-none">
          <span className="block text-sm font-bold text-tech-text mb-1">
            {rank.emoji} {rank.title}
          </span>
          <span className="block text-xs text-tech-text-muted mb-2">{rank.description}</span>
          <span className="block text-xs text-tech-cyan font-semibold mb-1">{score.toLocaleString()} pts</span>
          {nextRank && (
            <>
              <span className="block text-xs text-tech-text-muted mb-1">
                Next: {nextRank.emoji} {nextRank.title} ({nextRank.minScore.toLocaleString()} pts)
              </span>
              <span className="block w-full h-1 rounded-full bg-white/10 overflow-hidden">
                <span
                  className="block h-full rounded-full bg-gradient-to-r from-tech-cyan to-blue-400"
                  style={{ width: `${progressToNext}%` }}
                />
              </span>
              <span className="block text-[10px] text-tech-text-muted mt-0.5">{progressToNext}% to next rank</span>
            </>
          )}
          {!nextRank && (
            <span className="block text-xs text-amber-300 font-semibold">Maximum rank achieved!</span>
          )}
        </span>
      )}
    </span>
  );
};

export default RankBadge;
